<template>
	<div class="user-create">
		<Button class="user-create__activate-button" size="large" type="primary" @click="onShowForm">
			<Icon type="plus" />
			Создать пользователя
		</Button>

		<Modal v-model="visibleForm" :confirmLoading="loading" :width="1000" :maskClosable="false" title="Создание пользователя" centered>
			<div class="form">
				<Row type="flex" justify="start">
					<Col :span="8">
						<div class="form__section">
							<div class="form__section-label" @click="onVisibleFields('visibleBaseFields')">
								Основные данные
								<Icon :type="!visibleBaseFields ? 'up' : 'down'" class="icon" />
							</div>
							<FormItem
								v-for="{ name, label, type, options, icon, required } of baseFields"
								:key="name"
								:label="label"
								:type="type"
								:options="options"
								:icon="icon"
								:required="required"
								:visibleFields="visibleBaseFields"
								:error="errors[name]"
								v-model="form[name]"
							/>
						</div>

						<div class="form__section">
							<div class="form__section-label" @click="onVisibleFields('visibleProfileFields')">
								Персональные данные
								<Icon :type="!visibleProfileFields ? 'up' : 'down'" class="icon" />
							</div>
							<FormItem
								v-for="{ name, label, type, options, icon, required } of profileFields"
								:key="name"
								:label="label"
								:type="type"
								:options="options"
								:icon="icon"
								:required="required"
								:visibleFields="visibleProfileFields"
								:error="errors[name]"
								v-model="form[name]"
							/>
						</div>
					</Col>

					<Col :span="8">
						<div class="form__section">
							<div class="form__section-label" @click="onVisibleFields('visibleCompanyFields')">
								Данные компании
								<Icon :type="!visibleCompanyFields ? 'up' : 'down'" class="icon" />
							</div>
							<FormItem
								v-for="{ name, label, type, options, icon, required } of companyFields"
								:key="name"
								:label="label"
								:type="type"
								:options="options"
								:icon="icon"
								:required="required"
								:visibleFields="visibleCompanyFields"
								:error="errors[name]"
								v-model="form[name]"
							/>
						</div>
					</Col>

					<Col :span="8">
						<div class="form__section">
							<div class="form__section-label" @click="onVisibleFields('visibleAccountFields')">
								Реквизиты компании
								<Icon :type="!visibleAccountFields ? 'up' : 'down'" class="icon" />
							</div>
							<FormItem
								v-for="{ name, label, type, options, icon, required } of accountFields"
								:key="name"
								:label="label"
								:type="type"
								:options="options"
								:icon="icon"
								:required="required"
								:visibleFields="visibleAccountFields"
								:error="errors[name]"
								v-model="form[name]"
							/>
						</div>
					</Col>
				</Row>
			</div>
			<template slot="footer">
				<a-button v-if="!visibleAllFields" @click="onVisibleAllFields()">Показать доп. поля</a-button>
				<a-button v-else @click="onNotVisibleAllFields()">Скрыть доп. поля</a-button>
				<a-button key="back" @click="onCancel()">Отменить</a-button>
				<a-button key="submit" type="primary" @click="onCreate">Создать</a-button>
			</template>
		</Modal>
	</div>
</template>

<script>
import { Row, Col, Button, Icon, Modal } from "ant-design-vue"
import FormItem from "./FormItem"

/**
 * Создать поле
 */
const createField = (section, name, label, params) => {
	params = params || {}

	const type = params.type || "text"
	const icon = params.icon || null
	const required = params.required || false
	const options = params.options || []

	return { section, name, label, type, options, icon, required }
}

/**
 * Получить карту полей (для значений и ошибок полей)
 */
const getFieldsMap = () => {
	return {
		email: "",
		password: "",
		password_repeat: "",
		product_id: "",
		first_name: "",
		middle_name: "",
		last_name: "",
		phone: "",
		company_short: "",
		company_full: "",
		position: "",
		address_legal: "",
		address_actual: "",
		ogrn: "",
		inn: "",
		contract_number: "",
		date_conclusion_contract: "",
		bank_name: "",
		bik: "",
		kpp: "",
		account_current: "",
		account_corr: ""
	}
}

export default {
	name: "UserCreate",
	components: {
		Row,
		Col,
		Button,
		Icon,
		Modal,
		FormItem
	},
	data() {
		return {
			loading: false,
			visibleForm: false,
			visibleBaseFields: false,
			visibleProfileFields: false,
			visibleCompanyFields: false,
			visibleAccountFields: false,
			form: getFieldsMap(),
			errors: getFieldsMap(),
			products: []
		}
	},
	computed: {
		/**
		 * Основные данные
		 */
		baseFields() {
			const productOptions = this.products.map(({ id, name }) => ({ value: id, text: name }))

			return [
				createField("base", "email", "Email", { icon: "mail", required: true }),
				createField("base", "password", "Пароль", { type: "password", icon: "key", required: true }),
				createField("base", "password_repeat", "Повторный пароль", { type: "password", icon: "key", required: true }),
				createField("base", "product_id", "Продукт", { type: "select", options: productOptions, icon: "tag", required: true })
			]
		},

		/**
		 * Персональные данные
		 */
		profileFields() {
			return [
				createField("profile", "first_name", "Имя", { icon: "user", required: true }),
				createField("profile", "middle_name", "Отчество", { icon: "user" }),
				createField("profile", "last_name", "Фамилия", { icon: "user", required: true }),
				createField("profile", "phone", "Телефон", { icon: "phone" })
			]
		},

		/**
		 * Данные компании
		 */
		companyFields() {
			return [
				createField("company", "company_short", "Короткое название компании", { required: true }),
				createField("company", "company_full", "Полное название компании"),
				createField("company", "position", "Должность"),
				createField("company", "address_legal", "Юридический адрес"),
				createField("company", "address_actual", "Фактический адрес"),
				createField("company", "ogrn", "ОГРН"),
				createField("company", "inn", "ИНН"),
				createField("company", "contract_number", "Номер договора"),
				createField("company", "date_conclusion_contract", "Номер договора")
			]
		},

		/**
		 * Реквизиты компании
		 */
		accountFields() {
			return [
				createField("account", "bank_name", "Банк"),
				createField("account", "bik", "БИК"),
				createField("account", "kpp", "КПП"),
				createField("account", "account_current", "Расчетный счет"),
				createField("account", "account_corr", "Корреспондентский счет")
			]
		},

		/**
		 * Показать/Скрыть все поля
		 */
		visibleAllFields() {
			return this.visibleBaseFields && this.visibleProfileFields && this.visibleCompanyFields && this.visibleAccountFields
		}
	},

	watch: {
		// Если меняем
		visibleForm() {
			if (!this.visibleForm) {
				this.onCancel()
			}
		}
	},

	methods: {
		/**
		 * Показать форму
		 */
		onShowForm() {
			this.visibleForm = true
		},

		/**
		 * Обработать отмену
		 */
		onCancel() {
			this.form = getFieldsMap()
			this.errors = getFieldsMap()
			this.visibleForm = false
		},

		/**
		 * Обработать создание пользователя
		 */
		async onCreate() {
			this.errors = getFieldsMap()

			try {
				await this.$context.adminUserService.createUser(this.form)

				this.visibleForm = false
				this.loading = false
				this.form = getFieldsMap()
				this.$alert.success("Пользователь успешно создан")
				this.$emit("success")
			} catch ({ errors, message }) {
				this.errors = errors
				this.$alert.error(message)
			}
		},

		/**
		 * Получить продукты
		 */
		async getProducts() {
			try {
				this.products = await this.$context.adminProductService.getProducts()
			} catch (message) {
				this.$alert.error(message)
			}
		},

		/**
		 * Показать/Скрыть необязательные поля у колонок
		 */
		onVisibleFields(visibleFields) {
			this[visibleFields] = !this[visibleFields]
		},

		/**
		 * Показать все необязательные поля у колонок
		 */
		onVisibleAllFields() {
			this.visibleBaseFields = true
			this.visibleProfileFields = true
			this.visibleCompanyFields = true
			this.visibleAccountFields = true
		},

		/**
		 * Скрыть все необязательные поля у колонок
		 */
		onNotVisibleAllFields() {
			this.visibleBaseFields = false
			this.visibleProfileFields = false
			this.visibleCompanyFields = false
			this.visibleAccountFields = false
		}
	},
	mounted() {
		this.getProducts()
	}
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_vars";

.user-create {
	padding: 20px 0;

	&__activate-button {
		margin-right: 15px;
	}
}

.form {
	&__section {
		margin-bottom: 10px;
		padding: 10px;

		&-label {
			margin-bottom: 10px;
			padding-left: 5px;
			font-size: 18px;
			color: $primary-color;
			&:hover {
				cursor: pointer;
			}
		}
	}
}
</style>
